import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  Divider,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { useQuery } from 'react-query';

import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import ApplicantChecklistService from '../../../services/applicantChecklistService';
import useIntl from '../../../hooks/useIntl';
import messages from '../messages';

export default function useApplicationChecklistModal({
  checklistItems,
  applicationDecisionStatuses,
  applicant,
  handleMultipleChecklistOptionsChange,
  handleClose,
  isResident,
  propertyId,
  organizationId,
  isCanceled,
}) {
  const intl = useIntl();
  const checklistGridSize = 7.5;
  const decisionGridSize = 4.5;
  const { applicantChecklistItems } = useApplicantChecklistItems({
    applicantId: applicant.id,
    isResident,
    propertyId,
    organizationId,
  });
  const [saving, setSaving] = useState(false);

  const checklistItemsToUse = useMemo(() => {
    const approvedChecklistItems =
      applicantChecklistItems?.map((item) => ({
        ...item,
        decisionStatus: applicationDecisionStatuses?.find(
          (status) => status.name === 'Approved',
        )?.id,
      })) ?? [];

    return isResident ? approvedChecklistItems : checklistItems;
  }, [
    isResident,
    applicantChecklistItems,
    checklistItems,
    applicationDecisionStatuses,
  ]);

  const defaultValues = useMemo(() => {
    const values = {};
    checklistItemsToUse?.forEach((item) => {
      values[_.camelCase(`${item.name} Decision`)] =
        applicationDecisionStatuses?.find((status) => {
          const deprecatedDecisionStatus = item?.checklistItemOptions.find(
            (opt) => opt.selected,
          )?.decisionStatus;
          const decisionStatus =
            item.decisionStatus || deprecatedDecisionStatus;
          return status.id === decisionStatus;
        })?.id;
    });
    return values;
  }, [applicationDecisionStatuses, checklistItemsToUse]);

  const reactHookFormProps = useReactHookFormProps({
    shouldUnregister: true,
    defaultValues,
  });

  useEffect(() => {
    reactHookFormProps.reset(defaultValues);
  }, [defaultValues]);

  const computedFields = useMemo(
    () =>
      checklistItemsToUse
        ?.map((item) => [
          {
            variant: 'custom',
            component: (
              <Typography>
                {intl.formatMessage(messages.reviewedItem, {
                  itemName: item.name,
                })}
              </Typography>
            ),
            GridProps: {
              xs: checklistGridSize,
              display: 'flex',
              alignItems: 'center',
            },
          },
          {
            name: _.camelCase(`${item.name} Decision`),
            variant: 'singleSelect',
            options: applicationDecisionStatuses?.map((decision) => ({
              value: decision.id,
              text: decision.name,
              disabled: false,
            })),
            GridProps: {
              xs: decisionGridSize,
              display: 'flex',
              alignItems: 'center',
            },
            disabled: isResident || isCanceled,
            helperText: null,
          },
          {
            variant: 'custom',
            component: (
              <Divider
                variant="fullWidth"
                sx={{ backgroundColor: 'divider' }}
              />
            ),
            GridProps: {
              xs: 12,
            },
          },
        ])
        .flat(),
    [
      checklistItemsToUse,
      intl,
      applicationDecisionStatuses,
      isResident,
      isCanceled,
    ],
  );

  function cleanUp(hardReset = false) {
    setSaving(false);
    const values = hardReset ? null : reactHookFormProps.getValues();
    reactHookFormProps.reset(values);
    handleClose();
  }

  function handleSave() {
    setSaving(true);
    const submitValues = reactHookFormProps.getValues();
    const checklistItemsWithSubmitValues = checklistItemsToUse
      ?.map((item) => {
        const itemDecisionFormFieldName = _.camelCase(`${item.name} Decision`);
        const checklistItemId = item.id;
        const applicantChecklistDecisionId =
          submitValues[itemDecisionFormFieldName];
        const decisionFieldIsDirty = reactHookFormProps.getFieldState(
          itemDecisionFormFieldName,
        ).isDirty;

        return {
          checklistItemId,
          applicantChecklistDecisionId,
          isDirty: decisionFieldIsDirty,
        };
      })
      .filter((item) => item.isDirty)
      .map(({ isDirty, ...item }) => item);

    handleMultipleChecklistOptionsChange(
      applicant,
      checklistItemsWithSubmitValues,
      cleanUp,
    );
  }

  const formFields = [
    // Headers
    {
      variant: 'custom',
      component: <Typography variant="labelLarge">Checklist</Typography>,
      GridProps: {
        xs: checklistGridSize,
      },
    },
    {
      variant: 'custom',
      component: <Typography variant="labelLarge">Decision</Typography>,
      GridProps: {
        xs: decisionGridSize,
      },
    },
    {
      variant: 'custom',
      component: (
        <Divider
          variant="fullWidth"
          sx={{
            backgroundColor: 'divider',
          }}
        />
      ),
      GridProps: {
        xs: 12,
      },
    },
    ...computedFields,
  ];
  return {
    reactHookFormProps,
    formFields,
    handleSave,
    saving,
    cleanUp,
  };
}

const useApplicantChecklistItems = ({
  applicantId,
  isResident,
  organizationId,
  propertyId,
}) => {
  const queryKey = 'applicantChecklistItems';
  const applicantChecklistService = new ApplicantChecklistService();
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      applicantChecklistService.getChecklistByApplicantId({
        applicantId,
        organizationId,
        propertyId,
      }),
    { enabled: isResident, refetchOnWindowFocus: false },
  );

  return {
    applicantChecklistItems: data,
    isLoading,
  };
};
